import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { adminApi } from '../../lib/adminApi';
import type { AdminUserPatch } from '../../lib/adminApi';
import type { ApiAdminUser } from '../../types/admin';
import { LoadingSpinner } from '../common/Loading';
import { ErrorMessage } from '../common/Error';
import { Button } from '../common/Button';
import { toast } from 'sonner';

const PAGE_SIZE = 20;

export const AdminUsersPage: React.FC = () => {
  const { user: me } = useAuth();
  const [users, setUsers] = useState<ApiAdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [deltas, setDeltas] = useState<Record<string, string>>({});

  const load = useCallback(async (q: string, p: number) => {
    setLoading(true);
    setError(null);
    try {
      const { users: list } = await adminApi.listUsers({ q: q || undefined, page: p, limit: PAGE_SIZE });
      setUsers(list);
      setHasMore(list.length === PAGE_SIZE);
    } catch (err) {
      console.error('AdminUsers: erro ao listar users', err);
      setError('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(query, page);
  }, [page, load]);

  const patchUser = async (target: ApiAdminUser, patch: AdminUserPatch, successMsg: string) => {
    try {
      const { user: updated } = await adminApi.patchUser(target.id, patch);
      setUsers(prev => prev.map(u => (u.id === updated.id ? { ...u, ...updated } : u)));
      toast.success(successMsg);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao atualizar usuário');
      console.error('AdminUsers: patchUser falhou', err);
    }
  };

  const handleSubmitDelta = (target: ApiAdminUser) => {
    const raw = deltas[target.id];
    const delta = Number(raw);
    if (!raw || Number.isNaN(delta) || delta === 0) {
      toast.error('Digite um delta de créditos (ex.: 10 ou -5)');
      return;
    }
    patchUser(target, { creditsAdd: delta }, `Créditos ${delta > 0 ? '+' : ''}${delta} aplicados`);
    setDeltas(prev => ({ ...prev, [target.id]: '' }));
  };

  const handleToggleActive = (target: ApiAdminUser) => {
    const next = !target.isActive;
    if (!next && !window.confirm(`Desativar ${target.email}? O usuário perde o acesso.`)) return;
    patchUser(target, { isActive: next }, next ? 'Usuário reativado' : 'Usuário desativado');
  };

  const handleRoleChange = (target: ApiAdminUser, role: string) => {
    if (!['user', 'consultant', 'admin'].includes(role)) return;
    if (target.id === me?.id && role !== 'admin') {
      if (!window.confirm('Remover seu próprio acesso de admin? Você será expulso do painel.')) return;
    }
    patchUser(target, { role: role as ApiAdminUser['role'] }, `Role alterada para ${role}`);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        <h2 className="text-2xl font-bold text-purple-200">Usuários</h2>
        <form
          className="flex gap-2 md:ml-auto"
          onSubmit={(e) => { e.preventDefault(); setPage(1); load(query, 1); }}
        >
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por email ou nome..."
            className="input-primary"
          />
          <Button type="submit" size="sm">Buscar</Button>
        </form>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><LoadingSpinner /></div>
      ) : error ? (
        <ErrorMessage message={error} />
      ) : (
        <div className="space-y-3">
          {users.length === 0 && (
            <p className="text-purple-200/70 py-4 text-center">Nenhum usuário encontrado</p>
          )}
          {users.map((target) => (
            <div key={target.id} className="bg-white/5 rounded-lg p-4 flex flex-col md:flex-row md:items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-purple-200 truncate">{target.fullName}</p>
                <p className="text-sm text-purple-200/70 truncate">{target.email}</p>
                <p className="text-xs text-purple-200/50">
                  {target.credits} créditos · {target.isActive ? 'ativo' : 'inativo'} · desde {new Date(target.createdAt).toLocaleDateString('pt-BR')}
                </p>
              </div>

              <select
                value={target.role}
                onChange={(e) => handleRoleChange(target, e.target.value)}
                className="input-primary md:w-40"
              >
                <option value="user">user</option>
                <option value="consultant">consultant</option>
                <option value="admin">admin</option>
              </select>

              <div className="flex gap-2">
                <input
                  value={deltas[target.id] ?? ''}
                  onChange={(e) => setDeltas(prev => ({ ...prev, [target.id]: e.target.value }))}
                  placeholder="+/- créditos"
                  className="input-primary w-28"
                />
                <Button size="sm" variant="outline" onClick={() => handleSubmitDelta(target)}>
                  Aplicar
                </Button>
              </div>

              <Button
                size="sm"
                variant={target.isActive ? 'secondary' : 'outline'}
                onClick={() => handleToggleActive(target)}
              >
                {target.isActive ? 'Desativar' : 'Reativar'}
              </Button>
            </div>
          ))}

          <div className="flex items-center justify-center gap-3 pt-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
              ← Anterior
            </Button>
            <span className="text-sm text-purple-200/70">Página {page}</span>
            <Button size="sm" variant="outline" disabled={!hasMore} onClick={() => setPage(p => p + 1)}>
              Próxima →
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};